import React from 'react';
import { PopOver } from '@/components/design-system/molecules/pop-over';
import { RoomAssignmentRoomTableCellProps } from './types';
import { CountryIcon, SocialIcon, ColocIcon } from '@/components/design-system/atoms/icon';
import { isCountryIconName } from '@/components/design-system/atoms/icon/country-icon';
import { CountryIconName } from '@/components/design-system/atoms/icon/country-icon/types';
import { Avatar } from '@/components/design-system/atoms/avatar';
import { theme } from '@/utils/util_theme';
import { intercomNewConversationUrl } from '@/utils/util_helpers/intercom';
import { Badge } from '@/components/design-system/atoms/badge';
import { useTranslation } from 'react-i18next';

const RoomNumber = ({
  room,
  current_tenant,
  base_url,
}: {
  room: RoomAssignmentRoomTableCellProps['room'];
  current_tenant: RoomAssignmentRoomTableCellProps['current_tenant'];
  base_url: RoomAssignmentRoomTableCellProps['base_url'];
}) => {
  if (current_tenant) {
    return (
      <a
        target="_blank"
        rel="noreferrer"
        className="underline"
        href={`${base_url}/admin/contracts/${current_tenant?.contract_id}`}
      >
        {current_tenant?.name
          ? `#${room.room_number} - ${current_tenant?.name}`
          : `#${room.room_number}`}
      </a>
    )
  }
  return `#${room.room_number}`;
};

const RoomAssignmentRoomCellContent: React.FC<RoomAssignmentRoomTableCellProps> =
  ({ room, current_tenant, base_url, new_room }) => {
    const { t } = useTranslation();
    return (
      <PopOver placement="bottom-start">
        <PopOver.PopOverTrigger>
          <div className='flex flex-row overflow-hidden py-2 text-body font-normal'>
            <div className="flex flex-col flex-wrap overflow-hidden py-2 text-body font-normal">
              <span
                className="
                  line-clamp-2 break-all font-medium text-text-primaryHeavy"
                style={{ whiteSpace: 'initial' }}
              >
                <RoomNumber
                  room={room}
                  current_tenant={current_tenant}
                  base_url={base_url}
                />
              </span>
              <span
                className="line-clamp-1 break-all text-text-primaryLight"
                style={{ whiteSpace: 'initial' }}
              >
                {`${room.house.name}`}
              </span>
            </div>

            {
              current_tenant?.roles && current_tenant?.roles.length > 0 && (
                <ColocIcon
                  name="information-solid"
                  size={24}
                  className="text-text-successHeavy flex items-center justify-center"
                />
              )
            }
          </div>
        </PopOver.PopOverTrigger>
        <PopOver.PopOverContent className="room-popover">
          {
            current_tenant && (
              <div className="flex flex-row items-center justify-between">
                <Avatar src={current_tenant.avatar_url} type="person" />

                {current_tenant?.roles.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {current_tenant?.roles.map((role) => (
                    <Badge
                      key={role}
                      label={t(
                        `js.features.admin.contracts.room_assignment.table_cells.tenant_roles.${role}`
                      )}
                      variant="success"
                      size="medium"
                      pill
                      className="font-medium capitalize"
                    />
                    ))}
                  </div>
                )}
              </div>
            )
          }

          <div className="room-title">
            <span className="room--number">
              <RoomNumber
                room={room}
                current_tenant={current_tenant}
                base_url={base_url}
              />
            </span>
            <span className="house-number">{`${room.house.name}`}</span>
          </div>
          {current_tenant?.phone_number && (
            <div className="contact-info">
              <div className="contact-content">
                {isCountryIconName(
                  current_tenant?.phone_number.country_code ?? ''
                ) && (
                  <CountryIcon
                    className="pr-2"
                    name={
                      current_tenant?.phone_number
                        .country_code as CountryIconName
                    }
                  />
                )}
                <a
                  target="_blank"
                  href={intercomNewConversationUrl(
                    current_tenant?.intercom_user_id
                  )}
                  className="contact-text"
                  rel="noreferrer"
                >
                  {current_tenant?.phone_number.number}
                </a>
              </div>
              <div className="contact-actions">
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={`https://wa.me/${current_tenant?.phone_number.number}`}
                >
                  <SocialIcon
                    name="whatsapp"
                    size={20}
                    variant="black-white"
                    strokeColor={theme?.colors.J300}
                    hoverStrokeColor={theme?.colors.J200}
                  />
                </a>

                <button
                  onClick={() => {
                    navigator.clipboard.writeText(current_tenant?.phone_number.number);
                  }}
                >
                  <ColocIcon name="copy" />
                </button>
              </div>

            </div>
          )}

          {current_tenant?.email && (
            <div className="contact-info">
              <div className="contact-content">
                <ColocIcon name="envelope" />
                <a
                  target="_blank"
                  href={intercomNewConversationUrl(
                    current_tenant?.intercom_user_id
                  )}
                  className="contact-text"
                  rel="noreferrer"
                >
                  {current_tenant?.email}
                </a>
              </div>

              <div className="contact-actions">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(current_tenant?.email);
                  }}
                >
                  <ColocIcon name="copy" />
                </button>
              </div>
            </div>
          )}
        </PopOver.PopOverContent>
      </PopOver>
    );
  };

export { RoomAssignmentRoomCellContent };
